/*
 * Gaming page (templates/page.gaming.json).
 *
 * <vs-gaming-loadout>  sections/vs-gaming-loadout.liquid: three option groups (platform, style,
 *                      budget) and the loadout panel, with one "Add loadout to cart" button.
 * <vs-gaming-lineup>   vs-featured-collection with "Gaming lineup" on: All / Shooters / RPGs / Both filter
 *                      over the product cards.
 *
 * Loaded by both sections, so each element is defined once.
 */
(() => {
  const BUDGET_CAP = { low: 20000, mid: 30000, high: 50000 };

  /* Shopify money format ("€{{amount_with_comma_separator}}" etc.), with trailing zeros dropped like
   * money_without_trailing_zeros: "€139", not "€139,00". */
  const money = (cents, format) => {
    const fmt = format || (window.theme && theme.settings && theme.settings.moneyFormat) || '€{{amount}}';
    const match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    const kind = match ? match[1] : 'amount';
    const group = (number, decimals, thousands, decimal) => {
      const parts = (number / 100).toFixed(decimals).split('.');
      const whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      const fraction = parts[1] && parts[1] !== '00' ? decimal + parts[1] : '';
      return whole + fraction;
    };
    let value;
    switch (kind) {
      case 'amount_no_decimals':
        value = group(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = group(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = group(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = group(cents, 2, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = group(cents, 2, "'", '.');
        break;
      default:
        value = group(cents, 2, ',', '.');
    }
    return match ? fmt.replace(match[0], value) : value;
  };

  /* "A", "A and B", "A, B and C" */
  const joinList = (items, and) => {
    if (items.length <= 1) return items.join('');
    return `${items.slice(0, -1).join(', ')} ${and} ${items[items.length - 1]}`;
  };

  /* Shopify's `t` filter HTML-escapes translations ("PC &amp; Mac", "don&#39;t"); the script puts
   * them in with textContent, so decode them once. */
  const decode = (value) => {
    if (typeof value === 'string') {
      const area = document.createElement('textarea');
      area.innerHTML = value;
      return area.value;
    }
    if (value && typeof value === 'object') {
      Object.keys(value).forEach((key) => {
        value[key] = decode(value[key]);
      });
    }
    return value;
  };

  const readJson = (root, selector, fallback) => {
    const node = root.querySelector(selector);
    if (!node) return fallback;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('[vs-gaming] could not read', selector, error);
      return fallback;
    }
  };

  if (!customElements.get('vs-gaming-loadout')) {
    class VsGamingLoadout extends HTMLElement {
      connectedCallback() {
        this.products = readJson(this, '[data-vs-loadout-products]', []);
        this.extra = readJson(this, '[data-vs-loadout-extra]', { strings: {} });
        this.strings = decode(this.extra.strings || {});
        this.state = { platform: ['pc'], style: 'shooters', budget: 'mid' };
        this.skip = { cable: false, tips: false };

        const pressed = (key) => Array.from(this.querySelectorAll(`.vs-chip[data-key="${key}"][aria-pressed="true"]`)).map((chip) => chip.dataset.value);
        const platforms = pressed('platform');
        if (platforms.length) this.state.platform = platforms;
        this.state.style = pressed('style')[0] || this.state.style;
        this.state.budget = pressed('budget')[0] || this.state.budget;

        this.querySelectorAll('.vs-chip').forEach((chip) => {
          chip.addEventListener('click', () => this.choose(chip));
        });

        this.querySelectorAll('[data-toggle]').forEach((button) => {
          button.addEventListener('click', () => {
            const part = button.dataset.toggle;
            this.skip[part] = !this.skip[part];
            this.render();
          });
        });

        this.ref('add').addEventListener('click', () => this.addToCart());
        this.render();
      }

      ref(name) {
        return this.querySelector(`[data-ref="${name}"]`);
      }

      money(cents) {
        return money(cents, this.extra.money_format);
      }

      /* Platform is multi-select (at least one stays on); style and budget are single. */
      choose(chip) {
        const { key, value } = chip.dataset;
        if (chip.closest('[data-multi]')) {
          const list = this.state[key];
          const on = list.includes(value);
          if (on && list.length === 1) return;
          this.state[key] = on ? list.filter((v) => v !== value) : list.concat(value);
        } else {
          this.state[key] = value;
        }
        const selected = [].concat(this.state[key]);
        this.querySelectorAll(`.vs-chip[data-key="${key}"]`).forEach((c) => {
          c.setAttribute('aria-pressed', selected.includes(c.dataset.value) ? 'true' : 'false');
        });
        this.render();
      }

      pick() {
        const cap = BUDGET_CAP[this.state.budget];
        const byPriceDesc = (a, b) => b.price - a.price;
        const style = this.products.filter((p) => p.gaming === this.state.style);

        let pick = style.filter((p) => p.price <= cap).sort(byPriceDesc)[0];
        if (!pick) pick = this.products.filter((p) => p.gaming === 'both' && p.price <= cap).sort(byPriceDesc)[0];
        if (!pick) pick = style.sort((a, b) => a.price - b.price)[0];
        return pick || null;
      }

      /* One plug row for USB-C devices (PC, phone), one for 3.5 mm devices (consoles, Deck). */
      plugsFor(pick) {
        const s = this.strings;
        const device = s.device || {};
        const order = ['pc', 'phone', 'ps5', 'xbox', 'switch', 'deck'];
        const chosen = order.filter((p) => this.state.platform.includes(p));
        const usbDevices = chosen.filter((p) => p === 'pc' || p === 'phone');
        const jackDevices = chosen.filter((p) => p !== 'pc' && p !== 'phone');
        const names = (list) => joinList(list.map((p) => device[p] || p), device.and || 'and');

        let usbc = null;
        if (usbDevices.length) {
          const cable = pick.cable || this.extra.cable;
          if (pick.dac || !cable) {
            usbc = { title: s.usbc_in_box, line: (s.usbc_in_box_line || '').replace('[devices]', names(usbDevices)), price: 0 };
          } else {
            usbc = { title: cable.title, line: s.plug_cable_line, price: cable.price, variant: cable.variant, img: cable.img, alt: cable.alt, removable: true };
          }
        }
        const jack = jackDevices.length ? { line: (s.jack_line || '').replace('[devices]', names(jackDevices)) } : null;
        const upgrade = jackDevices.includes('ps5') || jackDevices.includes('switch');
        return { usbc, jack, upgrade };
      }

      render() {
        const s = this.strings;
        const pick = this.pick();
        this.current = null;
        if (!pick) {
          this.ref('add').disabled = true;
          return;
        }

        const plugs = this.plugsFor(pick);
        const tips = pick.tips || this.extra.tips || null;
        const cableOn = !!(plugs.usbc && plugs.usbc.variant && !this.skip.cable);
        const tipsOn = !!(tips && !this.skip.tips);
        this.current = { pick, plugs, tips, cableOn, tipsOn };

        const platformNames = this.state.platform.map((p) => (s.platform || {})[p]).filter(Boolean);
        const styleName = (s.style || {})[this.state.style] || '';
        this.ref('summary').textContent = [platformNames.join(', '), styleName].filter(Boolean).join(' · ');

        const img = this.ref('img');
        if (pick.img) {
          img.src = pick.img;
          img.alt = pick.alt || pick.title;
          img.hidden = false;
        } else {
          img.hidden = true;
        }

        this.ref('label').textContent = pick.label || '';
        this.ref('label').hidden = !pick.label;
        this.ref('title').textContent = pick.title;
        this.ref('price').textContent = this.money(pick.price);
        this.ref('line').textContent = pick.line || '';
        this.ref('line').hidden = !pick.line;

        const note = this.ref('note');
        const showNote = this.state.budget === 'high' && pick.price <= BUDGET_CAP.mid;
        note.textContent = showNote ? s.no_need || '' : '';
        note.hidden = !showNote;

        const usbcRow = this.ref('usbc');
        usbcRow.hidden = !plugs.usbc;
        if (plugs.usbc) {
          this.ref('usbc-title').textContent = plugs.usbc.title;
          this.ref('usbc-line').textContent = plugs.usbc.line;
          this.ref('usbc-price').textContent = plugs.usbc.price ? this.money(plugs.usbc.price) : s.included || '';
          this.setToggle('cable', usbcRow, plugs.usbc.removable);
          const usbcImg = this.ref('usbc-img');
          if (plugs.usbc.img) {
            usbcImg.src = plugs.usbc.img;
            usbcImg.alt = plugs.usbc.alt || plugs.usbc.title;
          }
          usbcImg.hidden = !plugs.usbc.img;
          this.ref('usbc-icon').hidden = !!plugs.usbc.img;
        }

        const tipsRow = this.ref('tips');
        tipsRow.hidden = !tips;
        if (tips) {
          this.ref('tips-title').textContent = tips.title;
          this.ref('tips-price').textContent = this.money(tips.price);
          const tipsImg = this.ref('tips-img');
          if (tips.img) {
            tipsImg.src = tips.img;
            tipsImg.alt = tips.alt || tips.title;
          }
          tipsImg.hidden = !tips.img;
          this.setToggle('tips', tipsRow, true);
        }

        const jackRow = this.ref('jack');
        jackRow.hidden = !plugs.jack;
        if (plugs.jack) this.ref('jack-line').textContent = plugs.jack.line;

        const upgrade = this.ref('upgrade');
        const consoleDac = pick.dongle || this.extra.console;
        if (plugs.upgrade && consoleDac && s.upgrade) {
          upgrade.textContent = s.upgrade.replace('[name]', consoleDac.title).replace('[price]', this.money(consoleDac.price));
          upgrade.hidden = false;
        } else {
          upgrade.hidden = true;
        }

        const total = pick.price + (cableOn ? plugs.usbc.price : 0) + (tipsOn ? tips.price : 0);
        this.ref('total').textContent = this.money(total);

        const more = this.ref('more');
        more.href = pick.url;
        more.textContent = (s.read_more || '').replace('[name]', pick.title);

        this.ref('add').disabled = false;
        this.ref('error').hidden = true;
      }

      /* "Remove" / "Add back" on a part the visitor may leave out; a removed row is dimmed and
       * its price is left out of the total and the cart. */
      setToggle(part, row, removable) {
        const button = this.querySelector(`[data-toggle="${part}"]`);
        if (!button) return;
        const removed = removable && this.skip[part];
        button.hidden = !removable;
        button.textContent = removed ? this.strings.add_back || '' : this.strings.remove || '';
        button.setAttribute('aria-pressed', removed ? 'true' : 'false');
        row.classList.toggle('is-removed', !!removed);
      }

      async addToCart() {
        if (!this.current) return;
        const { pick, plugs, tips, cableOn, tipsOn } = this.current;
        const items = [{ id: pick.variant, quantity: 1 }];
        if (cableOn) items.push({ id: plugs.usbc.variant, quantity: 1 });
        if (tipsOn && tips.variant) items.push({ id: tips.variant, quantity: 1 });

        const button = this.ref('add');
        const error = this.ref('error');
        const routes = (window.theme && theme.routes) || {};
        const addUrl = routes.cart_add_url || '/cart/add';
        const cartUrl = routes.cart_url || '/cart';

        button.setAttribute('aria-busy', 'true');
        button.disabled = true;
        error.hidden = true;

        try {
          const response = await fetch(`${addUrl}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items })
          });
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(result.description || 'cart add failed');

          const drawer = document.querySelector('cart-drawer');
          const cartIsPage = window.theme && theme.settings && theme.settings.cartType === 'page';
          if (!drawer || cartIsPage) {
            window.location.href = cartUrl;
            return;
          }

          const cart = await (await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })).json();
          if (theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'vs-gaming-loadout', cart });
          }
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
        } catch (err) {
          console.error('[vs-gaming]', err);
          error.textContent = this.strings.error || '';
          error.hidden = false;
        } finally {
          button.removeAttribute('aria-busy');
          button.disabled = false;
        }
      }
    }

    customElements.define('vs-gaming-loadout', VsGamingLoadout);
  }

  if (!customElements.get('vs-gaming-lineup')) {
    class VsGamingLineup extends HTMLElement {
      connectedCallback() {
        this.chips = Array.from(this.querySelectorAll('[data-vs-filter]'));
        this.items = Array.from(this.querySelectorAll('[data-gaming]'));
        this.chips.forEach((chip) => {
          chip.addEventListener('click', () => this.filter(chip.dataset.vsFilter));
        });
      }

      filter(value) {
        this.chips.forEach((chip) => {
          chip.setAttribute('aria-pressed', chip.dataset.vsFilter === value ? 'true' : 'false');
        });
        this.items.forEach((item) => {
          item.hidden = value !== 'all' && item.dataset.gaming !== value;
        });
      }
    }

    customElements.define('vs-gaming-lineup', VsGamingLineup);
  }
})();
